import { useCallback, useMemo } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { RepresentanteLegalUseCase } from '@/features/proposta/entities/Comprador/use-case/representante-legal-use-case'
import type { BaseRepresentanteLegalHookProps } from '@/features/proposta/interfaces/compradores.types'
import type { CompradorVinculoStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { showToast } from '@/infra/sonner'
import { StringUtils } from '@/utils'

export function useDadosRepresentanteLegalVinculo({
  onNext,
  compradores,
  refetch,
  nacionalidadePrincipalId,
}: BaseRepresentanteLegalHookProps) {
  const { trigger, setValue, watch, setError } = useFormContext<CompradorVinculoStepSchemaInput>()
  const compradorAtual = watch()
  const cpfRepresentante = compradorAtual.dados.cpf
  const buscouCpf = compradorAtual?.dados.buscouCpf
  const cpfRaw = cpfRepresentante.replaceAll(/\D/g, '')

  const pjVinculadaId = compradorAtual.compradorVinculadoIds?.[0]
  const compradorPJ = compradores.find((c) => c.id === pjVinculadaId)
  const useCase = useMemo(
    () =>
      new RepresentanteLegalUseCase(
        compradores,
        {
          dados: compradorAtual.dados,
          contato: compradorAtual.contato,
          estadoCivil: compradorAtual.estadoCivil,
          imagem: compradorAtual.imagem,
          tipo: compradorAtual.tipo,
          tipoDoCliente: compradorAtual.tipoDoCliente,
          emailsJaVinculados: compradorAtual.emailsJaVinculados,
        },
        compradorAtual.emailsJaVinculados,
        nacionalidadePrincipalId,
      ),
    [compradorAtual, compradores, nacionalidadePrincipalId],
  )

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)
    if (!ok) {
      if (error === 'MISSING_CPF') {
        trigger('dados.cpf')
        return
      }
      if (error === 'NEED_SEARCH') {
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
        })
        return
      }
    }
    if (await trigger('dados')) onNext()
  }, [cpfRaw, buscouCpf, trigger, onNext, useCase])

  const handleBuscarCliente = useCallback(async () => {
    if (!compradorPJ) return

    const haVinculosDuplicados = compradorPJ.vinculos.some(
      (vinculo) => vinculo.tipo === 'Representante' && StringUtils.normalize(vinculo.documento) === cpfRaw,
    )

    if (haVinculosDuplicados) {
      setError('dados.cpf', {
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_ja_vinculado'),
      })
      return
    }

    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        if (fetchResult.patch) {
          setValue('dados', fetchResult.patch.dados)
          setValue('contato', fetchResult.patch.contato)
          setValue('estadoCivil', fetchResult.patch.estadoCivil)
          setValue('imagem', fetchResult.patch.imagem)
        }
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw, compradorPJ, refetch, setValue, setError, useCase])

  const handleLimpar = useCallback(() => {
    const limpoFormulario = useCase.limparFormulario()
    if (limpoFormulario) {
      setValue('dados', limpoFormulario.dados)
      setValue('contato', limpoFormulario.contato)
      setValue('estadoCivil', limpoFormulario.estadoCivil)
      setValue('imagem', limpoFormulario.imagem)
    }
  }, [setValue, useCase])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
